/**
 * Recovery helpers for two situations users hit in practice:
 * 1. The session expired and the token refresh failed. Without a redirect the app
 *    keeps showing pages with empty data (every request is a 401).
 * 2. A new version was deployed while the tab was open, so lazily loaded page chunks
 *    from the old build no longer exist on the server.
 */

const RELOAD_FLAG = 'reloam:chunk-reload';

/** Where to send the user after a failed refresh, or null if already on an auth page. */
export function signInRedirectTarget(pathname: string, search = ''): string | null {
  if (pathname.startsWith('/auth/')) return null;
  const from = encodeURIComponent(`${pathname}${search}`);
  return `/auth/signin?expired=1&from=${from}`;
}

/**
 * Reload once when a chunk from an old deploy fails to load. The flag stops a reload
 * loop if the chunk is missing for another reason.
 */
export function reloadOnceForStaleChunk(storage: Pick<Storage, 'getItem' | 'setItem'>, reload: () => void): boolean {
  if (storage.getItem(RELOAD_FLAG)) return false;
  storage.setItem(RELOAD_FLAG, String(Date.now()));
  reload();
  return true;
}

/** Call after a successful start so a later deploy can trigger one reload again. */
export function clearStaleChunkFlag(storage: Pick<Storage, 'removeItem'>): void {
  storage.removeItem(RELOAD_FLAG);
}
